import { AvroOptional } from "@indeed/signals-logger";
import { log as logRNHiringEventOverviewShown } from "@indeed/signals-schema/com/indeed/signals/schema/hiringevents/RNHiringEventOverviewShown";
import React, { useEffect } from "react";
import { StyleSheet } from "react-native";

import { Divider, Box, Heading, Text } from "../../../../../../../mads";
import { useI18n } from "../../../../../../../shared/I18n";
import { useViewjobProctorAllocation } from "../../../../../../proctor/__generated__/ViewjobProctorContext";
import { HeRnViewjobOverviewTst } from "../../../../../../proctor/__generated__/ViewjobProctorSpecifications";
import {
  useHiringEventsData,
  useViewjobOneGraphModel,
} from "../../../../../context/ViewJobDataContext";
import ViewJobLabelledContent from "../common/ViewJobLabelledContent/ViewJobLabelledContent";
import { DateTimeSection, type DateTimeSectionProps } from "./DateTimeSection";
import { LocationSection, type LocationSectionProps } from "./LocationSection";
import { hiringEventsViewJobOverviewDataAdapter } from "./utils/hiringEventsViewJobOverviewDataAdapter";

export type HiringEventsViewJobOverviewProps = {
  estimatedDuration: string;
  hiringEventUuid: string;
  jobKey: string;
} & LocationSectionProps &
  DateTimeSectionProps;

export const HiringEventsViewJobOverview: React.FunctionComponent<
  HiringEventsViewJobOverviewProps
> = ({
  dateTimes,
  estimatedDuration,
  hiringEventUuid,
  interviewsOffered,
  jobKey,
  location,
  timezone,
}) => {
  const gt = useI18n();

  useEffect(() => {
    logRNHiringEventOverviewShown({
      jobkey: AvroOptional.string(jobKey),
      hiringEventUuid: AvroOptional.string(hiringEventUuid),
    });
  }, [jobKey, hiringEventUuid]);

  return (
    <>
      <Box
        id="HiringEventsViewJobOverviewSection"
        sx={styles.hiringEventsViewJobOverviewSectionContainer}
      >
        <Heading level={4}>
          {interviewsOffered
            ? gt.pgettext("View job overview - header - interview details", "Interview details")
            : gt.pgettext("View job overview - header - event details", "Event details")}
        </Heading>
        <LocationSection location={location} interviewsOffered={interviewsOffered} />
        <DateTimeSection dateTimes={dateTimes} timezone={timezone} />
        {!!estimatedDuration && (
          <ViewJobLabelledContent
            label={gt.pgettext(
              "View job overview - estimated duration for each time slot",
              "Estimated duration",
            )}
          >
            <Box>
              <Text level={3}>{estimatedDuration}</Text>
            </Box>
            <Text variant="secondary" level={1} sx={styles.estimatedDurationHelperText}>
              {gt.pgettext(
                "View job overview - estimated duration helper text",
                "Each candidate will have their own time slot.",
              )}
            </Text>
          </ViewJobLabelledContent>
        )}
      </Box>
      <Divider />
    </>
  );
};

export const HiringEventsViewJobOverviewWithContext: React.FC = () => {
  const gt = useI18n();
  const viewjobOneGraphModel = useViewjobOneGraphModel();
  const proctorAllocation = useViewjobProctorAllocation(HeRnViewjobOverviewTst);
  const hiringEventData = useHiringEventsData();

  if (!proctorAllocation.isTest || !hiringEventData || !viewjobOneGraphModel?.job.jobKey) {
    return null;
  }

  const hiringEventsViewJobOverviewProps = hiringEventsViewJobOverviewDataAdapter(
    gt,
    hiringEventData,
  );

  return (
    <HiringEventsViewJobOverview
      jobKey={viewjobOneGraphModel.job.jobKey}
      {...hiringEventsViewJobOverviewProps}
    />
  );
};

const styles = StyleSheet.create({
  hiringEventsViewJobOverviewSectionContainer: {
    marginVertical: 24,
  },
  estimatedDurationHelperText: {
    fontStyle: "italic",
  },
});
